import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Search, Pill, ArrowRight } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { PRODUCTS, CATEGORIES, SITE } from "../data/siteData";

const catBadge = {
  Oncology: "bg-rose-50 text-rose-700 border-rose-200",
  Gastroenterology: "bg-amber-50 text-amber-700 border-amber-200",
  Antibiotics: "bg-violet-50 text-violet-700 border-violet-200",
  "Nutraceuticals & Supportive Care": "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const Products = () => {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return PRODUCTS.filter((p) => {
      const inCat = category === "All" || p.category === category;
      const inQuery =
        !q ||
        p.brand.toLowerCase().includes(q) ||
        p.composition.toLowerCase().includes(q);
      return inCat && inQuery;
    });
  }, [query, category]);

  return (
    <div data-testid="products-page">
      <PageHeader
        testId="products-header"
        eyebrow="Medicines & Products"
        title="Our formulations catalogue"
        subtitle="From pioneering antineoplastic molecules to everyday gastroenterology, antibiotic and nutraceutical care — every brand carries the Cytocare promise of quality and affordability."
      />

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        {/* Search + filters */}
        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between" data-testid="products-controls">
          <div className="relative w-full max-w-md">
            <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by brand or molecule…"
              data-testid="products-search-input"
              className="w-full rounded-full border border-slate-300 bg-white py-3 pl-11 pr-4 text-sm outline-none transition-colors focus:border-[#1D4ED8] focus:ring-2 focus:ring-blue-100"
            />
          </div>
          <div className="flex flex-wrap gap-2" data-testid="products-category-filters">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                data-testid={`filter-${c.toLowerCase().replace(/[\s&]+/g, "-")}`}
                className={`rounded-full border px-4 py-2 text-xs font-semibold transition-colors duration-200 ${
                  category === c
                    ? "border-[#1D4ED8] bg-[#1D4ED8] text-white"
                    : "border-slate-300 bg-white text-slate-600 hover:border-[#1D4ED8] hover:text-[#1D4ED8]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <p className="mt-6 text-sm text-slate-500" data-testid="products-count">
          Showing {filtered.length} of {PRODUCTS.length} brands
        </p>

        {/* Grid */}
        <motion.div layout className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="products-grid">
          <AnimatePresence mode="popLayout">
            {filtered.map((p) => (
              <motion.article
                layout
                key={p.id}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.35 }}
                className="group flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-shadow duration-300 hover:shadow-xl"
                data-testid={`product-card-${p.id}`}
              >
                {p.image ? (
                  <div className="aspect-[16/10] overflow-hidden bg-slate-50">
                    <img
                      src={p.image}
                      alt={`${p.brand} — ${p.composition}`}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                ) : (
                  <div className="flex aspect-[16/10] items-center justify-center bg-[#0A1633]">
                    <div className="text-center">
                      <Pill size={26} className="mx-auto text-blue-300" />
                      <p className="mt-3 text-lg font-extrabold tracking-wide text-white">{p.brand}</p>
                      <p className="mt-1 px-6 text-xs text-slate-300">{p.composition}</p>
                    </div>
                  </div>
                )}
                <div className="flex flex-1 flex-col p-6">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-extrabold tracking-wide text-slate-900">{p.brand}</h3>
                      <p className="mt-1 text-xs leading-snug text-slate-500">{p.composition}</p>
                    </div>
                    <span className={`shrink-0 rounded-full border px-2.5 py-1 text-[10px] font-semibold ${catBadge[p.category]}`}>
                      {p.category}
                    </span>
                  </div>
                  <p className="mt-3 text-xs font-medium text-slate-400">{p.form}</p>
                  {p.benefits ? (
                    <ul className="mt-4 space-y-1.5">
                      {p.benefits.map((b) => (
                        <li key={b} className="flex items-start gap-2 text-sm text-slate-600">
                          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#1D4ED8]" />
                          {b}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-4 text-sm leading-relaxed text-slate-600">{p.desc}</p>
                  )}
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>

        {filtered.length === 0 && (
          <div className="mt-16 text-center" data-testid="products-empty">
            <p className="text-lg font-semibold text-slate-700">No products match your search</p>
            <p className="mt-2 text-sm text-slate-500">Try a different brand name, molecule or category.</p>
          </div>
        )}

        {/* Enquiry callout */}
        <div className="mt-20 flex flex-wrap items-center justify-between gap-6 rounded-3xl bg-[#0A1633] px-8 py-10 text-white" data-testid="products-enquiry-callout">
          <div>
            <h3 className="text-xl font-extrabold sm:text-2xl">Hospital, pharmacy or distribution enquiry?</h3>
            <p className="mt-2 text-sm text-slate-300">
              Write to us at {SITE.email} for bulk procurement, product literature and partnership opportunities.
            </p>
          </div>
          <Link
            to="/contact"
            data-testid="products-enquiry-button"
            className="inline-flex items-center gap-2 rounded-full bg-[#1D4ED8] px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#3B82F6]"
          >
            Send an enquiry <ArrowRight size={16} />
          </Link>
        </div>

        <p className="mt-8 text-center text-xs text-slate-400" data-testid="products-disclaimer">
          Product information is indicative for healthcare professionals. Please refer to the full prescribing information before use.
        </p>
      </section>
    </div>
  );
};

export default Products;
